import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import BulkCreateDistributorsDialog from '../components/BulkCreateDistributorsDialog';
import ResetDistributorPasswordsDialog from '../components/ResetDistributorPasswordsDialog';
import api, { getStaff, createStaff, deleteStaff, getAmazonBalances, getPendingUsers, updateUserStatus, getDistributors, createDistributor, deleteDistributor, updateDistributor, getAgencies, createAccountant, createSalesPerson, deleteSalesPerson, downloadDistributorTemplate, bulkUploadDistributors, updateUserProfile, getSubStaff, createSubStaff, deleteSubStaff } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Trash, AmazonLogo, CheckCircle, XCircle, Clock, Storefront, Calculator, UserCirclePlus, PencilSimple, UploadSimple, FileXls, Prohibit } from '@phosphor-icons/react';
import { toast } from 'sonner';
import VendorsSection from '../components/staff/VendorsSection';

export default function StaffPage({ view = 'all' }) {
  // view = 'staff' | 'distributors' | 'all' — drives which section renders.
  // Separate routes /staff and /distributors pass the matching prop.
  const showStaff = view === 'staff' || view === 'all';
  const showDistributors = view === 'distributors' || view === 'all';
  const showAccountants = view === 'accountants' || view === 'all';
  const showSalesPersons = view === 'all';   // dedicated /sales-persons page exists
  const showVendorsBlock = view === 'all';   // dedicated /gift-card-vendors page exists
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [amazonBalances, setAmazonBalances] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'staff' });

  // Distributor state
  const [distributors, setDistributors] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [distDialogOpen, setDistDialogOpen] = useState(false);
  const [distForm, setDistForm] = useState({ name: '', email: '', password: '', agency_ids: [], sales_person_id: '' });
  const [bulkCreateOpen, setBulkCreateOpen] = useState(false);
  const [resetPwdOpen, setResetPwdOpen] = useState(false);

  // Accountant state
  const [accountants, setAccountants] = useState([]);
  const [accDialogOpen, setAccDialogOpen] = useState(false);
  const [accForm, setAccForm] = useState({ name: '', email: '', password: '', mobile: '' });

  // Sales Person state
  const [salesPersons, setSalesPersons] = useState([]);
  const [spDialogOpen, setSpDialogOpen] = useState(false);
  const [spForm, setSpForm] = useState({ name: '', email: '', mobile: '', password: '', commission_rate: '' });

  // Sub-Staff state
  const [subStaff, setSubStaff] = useState([]);
  const [subStaffDialogOpen, setSubStaffDialogOpen] = useState(false);
  const [subStaffForm, setSubStaffForm] = useState({ name: '', email: '', password: '', mobile: '' });
  const [expandedTeamLead, setExpandedTeamLead] = useState(null);

  // Bulk upload state
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

  // Edit user state (extended for distributors: agency_ids + sales_person_id)
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', mobile: '', password: '', agency_ids: [], sales_person_id: '', low_balance_threshold: '' });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [staffRes, balanceRes, pendingRes, distRes, agRes] = await Promise.all([
        getStaff(),
        getAmazonBalances(),
        getPendingUsers(),
        getDistributors().catch(() => ({ data: [] })),
        getAgencies().catch(() => ({ data: [] }))
      ]);
      setStaff(staffRes.data);
      setPendingUsers(pendingRes.data);
      setDistributors(distRes.data);
      setAgencies(agRes.data);
      
      // Filter accountants and sales persons from staff list
      const allUsers = staffRes.data;
      setAccountants(allUsers.filter(u => u.role === 'accountant'));
      setSalesPersons(allUsers.filter(u => u.role === 'sales_person'));
      setSubStaff(allUsers.filter(u => u.role === 'sub_staff'));
      // Admin sees only staff/assistant/accountant/sales_person — NO sub_staff
      // Team Lead sees self + sub_staff
      setStaff(allUsers.filter(u => u.role !== 'accountant' && u.role !== 'sales_person' && u.role !== 'sub_staff'));
      
      const balanceMap = {};
      balanceRes.data.forEach(b => {
        balanceMap[b.staff_id] = b.balance;
      });
      setAmazonBalances(balanceMap);
    } catch (error) {
      toast.error('Failed to fetch staff');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createStaff(formData);
      toast.success('Staff created successfully');
      setDialogOpen(false);
      setFormData({ name: '', email: '', password: '', role: 'staff' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create staff');
    }
  };

  const handleCreateAccountant = async () => {
    if (!accForm.name || !accForm.email || !accForm.password) { toast.error('All fields required'); return; }
    try {
      await createAccountant(accForm);
      toast.success('Accountant created successfully');
      setAccDialogOpen(false);
      setAccForm({ name: '', email: '', password: '', mobile: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create accountant');
    }
  };

  const handleCreateSalesPerson = async () => {
    if (!spForm.name || !spForm.email || !spForm.password || !spForm.mobile) { toast.error('Name, Email, Mobile, Password required'); return; }
    try {
      await createSalesPerson({ ...spForm, commission_rate: parseFloat(spForm.commission_rate) || 0 });
      toast.success('Sales Person created!');
      setSpDialogOpen(false);
      setSpForm({ name: '', email: '', mobile: '', password: '', commission_rate: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create sales person');
    }
  };

  const handleDeleteSalesPerson = async (id) => {
    if (!window.confirm('Sales Person delete karna hai? Confirm karo.')) return;
    try {
      await deleteSalesPerson(id);
      toast.success('Sales Person deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this staff?')) return;
    try {
      await deleteStaff(id);
      toast.success('Staff deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete staff');
    }
  };

  const handleDistributorSubmit = async (e) => {
    e.preventDefault();
    if (distForm.agency_ids.length === 0) {
      toast.error('At least 1 agency select karo');
      return;
    }
    try {
      await createDistributor(distForm);
      toast.success('Distributor created!');
      setDistDialogOpen(false);
      setDistForm({ name: '', email: '', password: '', agency_ids: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create distributor');
    }
  };

  const handleDeleteDistributor = async (id) => {
    if (!window.confirm('Delete this distributor?')) return;
    try {
      await deleteDistributor(id);
      toast.success('Distributor deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete distributor');
    }
  };

  const toggleAgency = (agencyId) => {
    setDistForm(prev => ({
      ...prev,
      agency_ids: prev.agency_ids.includes(agencyId)
        ? prev.agency_ids.filter(id => id !== agencyId)
        : [...prev.agency_ids, agencyId]
    }));
  };

  const handleApprove = async (userId) => {
    try {
      await updateUserStatus(userId, 'approve');
      toast.success('User approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm('Reject this user? They will not be able to login.')) return;
    try {
      await updateUserStatus(userId, 'reject');
      toast.success('User rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject user');
    }
  };

  const handleBlock = async (userId, userName) => {
    const reason = window.prompt(`Block "${userName}"? Enter reason:`);
    if (reason === null) return;
    try {
      await api.put(`/users/${userId}/status`, { action: 'block', reason });
      toast.success(`${userName} blocked`);
      fetchData();
    } catch (error) {
      toast.error('Block failed');
    }
  };

  const handleUnblock = async (userId, userName) => {
    if (!window.confirm(`Unblock "${userName}"? They will be able to login again.`)) return;
    try {
      await api.put(`/users/${userId}/status`, { action: 'unblock' });
      toast.success(`${userName} unblocked`);
      fetchData();
    } catch (error) {
      toast.error('Unblock failed');
    }
  };

  const handleSuspend = async (userId, userName) => {
    const reason = window.prompt(`Suspend "${userName}"? Enter reason:`);
    if (reason === null) return;
    try {
      await api.put(`/users/${userId}/status`, { action: 'suspend', reason });
      toast.success(`${userName} suspended`);
      fetchData();
    } catch (error) {
      toast.error('Suspend failed');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Sub-Staff handlers
  const handleCreateSubStaff = async (e) => {
    e.preventDefault();
    try {
      await createSubStaff(subStaffForm);
      toast.success(`Sub-staff ${subStaffForm.name} created`);
      setSubStaffForm({ name: '', email: '', password: '', mobile: '' });
      setSubStaffDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteSubStaff = async (id) => {
    if (!window.confirm('Delete sub-staff?')) return;
    try { await deleteSubStaff(id); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Failed'); }
  };

  const isOwner = user?.role === 'admin' || user?.role === 'owner';
  const isAdminOrAbove = isOwner || user?.role === 'assistant';

  // Bulk upload handler
  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const res = await bulkUploadDistributors(file);
      setBulkResult(res.data);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBulkUploading(false);
      e.target.value = '';
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await downloadDistributorTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'distributor_bulk_template.xlsx';
      a.click();
    } catch { toast.error('Template download failed'); }
  };

  // Edit user handler
  const openEditUser = (u) => {
    setEditingUser(u);
    setEditForm({
      name: u.name || '',
      email: u.email || '',
      mobile: u.mobile || '',
      password: '',
      agency_ids: Array.isArray(u.agency_ids) ? [...u.agency_ids] : [],
      sales_person_id: u.sales_person_id || '',
      low_balance_threshold: (u.low_balance_threshold == null) ? '' : String(u.low_balance_threshold),
    });
    setEditDialogOpen(true);
  };

  const toggleEditAgency = (agencyId) => {
    setEditForm(prev => ({
      ...prev,
      agency_ids: prev.agency_ids.includes(agencyId)
        ? prev.agency_ids.filter(id => id !== agencyId)
        : [...prev.agency_ids, agencyId],
    }));
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    const isDistributor = editingUser.role === 'distributor';
    const payload = {};
    if (editForm.name && editForm.name !== editingUser.name) payload.name = editForm.name;
    if (editForm.email && editForm.email !== editingUser.email) payload.email = editForm.email;
    if (editForm.mobile !== (editingUser.mobile || '')) payload.mobile = editForm.mobile;
    if (editForm.password) payload.password = editForm.password;
    if (isDistributor) {
      const origIds = Array.isArray(editingUser.agency_ids) ? editingUser.agency_ids : [];
      const newIds = editForm.agency_ids || [];
      const changed = origIds.length !== newIds.length || origIds.some(id => !newIds.includes(id));
      if (changed) payload.agency_ids = newIds;
      if ((editForm.sales_person_id || '') !== (editingUser.sales_person_id || '')) {
        payload.sales_person_id = editForm.sales_person_id || '';
      }
      const origThr = editingUser.low_balance_threshold == null ? '' : String(editingUser.low_balance_threshold);
      const newThr = (editForm.low_balance_threshold || '').trim();
      if (newThr !== origThr) {
        if (newThr === '') {
          payload.low_balance_threshold = 5000; // reset to default
        } else {
          const n = Number(newThr);
          if (isNaN(n) || n < 0) {
            toast.error('Threshold valid number hona chahiye');
            return;
          }
          payload.low_balance_threshold = n;
        }
      }
    }
    if (Object.keys(payload).length === 0) { toast.info('Koi change nahi kiya'); return; }
    try {
      if (isDistributor) {
        await updateDistributor(editingUser.id, payload);
      } else {
        await updateUserProfile(editingUser.id, payload);
      }
      toast.success(`${editingUser.name} updated`);
      setEditDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  if (!isAdminOrAbove) {
    return <div className="p-4 text-red-600">Access denied. Admin only.</div>;
  }

  return (
    <div className="space-y-6" data-testid="staff-page">
      {showStaff && (<>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Staff Management</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-staff-btn">
              <Plus size={18} className="mr-2" /> Add Staff
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Staff</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="form-label">Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="form-input"
                  required
                  data-testid="staff-name-input"
                />
              </div>
              <div>
                <Label className="form-label">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="form-input"
                  required
                  data-testid="staff-email-input"
                />
              </div>
              <div>
                <Label className="form-label">Password</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="form-input"
                  required
                  minLength={6}
                  data-testid="staff-password-input"
                />
              </div>
              {isOwner && (
                <div>
                  <Label className="form-label">Role</Label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="form-input w-full border border-gray-200 rounded-md h-10 px-3 text-sm"
                    data-testid="staff-role-select"
                  >
                    <option value="staff">Staff</option>
                    <option value="assistant">Assistant</option>
                  </select>
                </div>
              )}
              <Button type="submit" className="w-full btn-primary" data-testid="staff-submit-btn">
                Create {formData.role === 'assistant' ? 'Assistant' : 'Staff'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pending Users Section */}
      {pendingUsers.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 p-4" data-testid="pending-users-section">
          <h2 className="text-base font-semibold text-amber-800 mb-3 flex items-center gap-2">
            <Clock size={20} />
            Pending Approvals ({pendingUsers.length})
          </h2>
          <div className="bg-white border border-amber-100 overflow-x-auto">
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Registered</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingUsers.map((u) => (
                  <TableRow key={u.id} data-testid={`pending-user-${u.id}`}>
                    <TableCell className="font-medium">
                      <div>{u.name}</div>
                      {u.role === 'distributor' && (u.agency_names?.length > 0 || u.oil_company) && (
                        <div className="text-[11px] text-gray-500 mt-0.5">
                          {u.agency_names?.length > 0 && <span>🏪 {u.agency_names.join(', ')}</span>}
                          {u.oil_company && <span className="ml-2">⛽ {u.oil_company}</span>}
                        </div>
                      )}
                      {u.role === 'distributor' && (u.sales_person_name || u.requested_sales_person_name) && (
                        <div className="text-[11px] mt-0.5">
                          {u.sales_person_name ? (
                            <span className="text-emerald-700">👤 SP auto-matched: <b>{u.sales_person_name}</b></span>
                          ) : (
                            <span className="text-amber-700">⚠️ SP requested: "{u.requested_sales_person_name}" (no match — assign manually)</span>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{formatDate(u.created_at)}</TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button
                        size="sm"
                        onClick={() => handleApprove(u.id)}
                        className="bg-green-600 hover:bg-green-700 text-white text-xs h-8 px-3"
                        data-testid={`approve-user-${u.id}`}
                      >
                        <CheckCircle size={14} className="mr-1" /> Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReject(u.id)}
                        className="text-red-600 border-red-300 hover:bg-red-50 text-xs h-8 px-3"
                        data-testid={`reject-user-${u.id}`}
                      >
                        <XCircle size={14} className="mr-1" /> Reject
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Active Staff */}
      <div className="card-flat overflow-x-auto">
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : (
          <Table className="table-dense">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amazon Balance</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {staff.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-gray-500">
                    No staff found
                  </TableCell>
                </TableRow>
              ) : (
                staff.map((s) => (
                  <React.Fragment key={s.id}>
                  <TableRow data-testid={`staff-row-${s.id}`}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell>{s.email}</TableCell>
                    <TableCell>
                      {s.role === 'assistant' ? (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5">Assistant</span>
                      ) : (
                        <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5">Staff</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {s.status === 'approved' ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Active</span>
                      ) : s.status === 'rejected' ? (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Rejected</span>
                      ) : s.status === 'blocked' ? (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded" title={s.blocked_reason}>Blocked</span>
                      ) : s.status === 'suspended' ? (
                        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded" title={s.blocked_reason}>Suspended</span>
                      ) : (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Pending</span>
                      )}
                    </TableCell>
                    <TableCell className="numeric">
                      <span className="flex items-center justify-end gap-1">
                        <AmazonLogo size={16} className="text-[#FF9900]" />
                        {formatCurrency(amazonBalances[s.id])}
                      </span>
                    </TableCell>
                    <TableCell>{formatDate(s.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        {s.role === 'staff' && (user?.role === 'staff') && (
                          <Button variant="ghost" size="sm" onClick={() => { setExpandedTeamLead(expandedTeamLead === s.id ? null : s.id); }} className="text-violet-600 hover:text-violet-700 text-xs gap-1" data-testid={`team-${s.id}`}>
                            <UserCirclePlus size={14} /> Team
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => openEditUser(s)} className="text-blue-600 hover:text-blue-700" data-testid={`edit-staff-${s.id}`}>
                          <PencilSimple size={16} />
                        </Button>
                        {(s.status === 'approved') && (
                          <Button variant="ghost" size="sm" onClick={() => handleBlock(s.id, s.name)} className="text-orange-600 hover:text-orange-700 text-xs" data-testid={`block-staff-${s.id}`} title="Block">
                            <Prohibit size={16} />
                          </Button>
                        )}
                        {(s.status === 'blocked' || s.status === 'suspended') && (
                          <Button variant="ghost" size="sm" onClick={() => handleUnblock(s.id, s.name)} className="text-green-600 hover:text-green-700 text-xs" data-testid={`unblock-staff-${s.id}`} title="Unblock">
                            <CheckCircle size={16} />
                          </Button>
                        )}
                        {isOwner && (
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(s.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-staff-${s.id}`}>
                            <Trash size={16} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  {/* Sub-Staff rows under this team lead */}
                  {expandedTeamLead === s.id && subStaff.filter(ss => ss.parent_staff_id === s.id).map(ss => (
                    <TableRow key={ss.id} className="bg-violet-50/50">
                      <TableCell className="pl-8 text-sm">
                        <span className="text-violet-400 mr-1">└</span> {ss.name}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">{ss.email}</TableCell>
                      <TableCell><span className="text-[10px] bg-violet-100 text-violet-700 px-2 py-0.5 rounded font-bold">SUB-STAFF</span></TableCell>
                      <TableCell><span className="text-xs bg-green-100 text-green-700 px-2 py-0.5">Active</span></TableCell>
                      <TableCell className="numeric"><span className="text-xs text-gray-400">—</span></TableCell>
                      <TableCell className="text-xs">{formatDate(ss.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="sm" onClick={() => openEditUser(ss)} className="text-blue-600"><PencilSimple size={14} /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteSubStaff(ss.id)} className="text-red-600"><Trash size={14} /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {expandedTeamLead === s.id && (
                    <TableRow className="bg-violet-50/30">
                      <TableCell colSpan={7} className="pl-8">
                        <Button size="sm" variant="outline" className="text-xs text-violet-600 border-violet-300 gap-1 h-7" onClick={() => { setExpandedTeamLead(s.id); setSubStaffDialogOpen(true); }}>
                          <UserCirclePlus size={12} /> Add Sub-Staff under {s.name}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </div>

      </>)}

      {/* Distributor Management (Owner Only) */}
      {showDistributors && isOwner && (
        <div className="space-y-4" data-testid="distributor-section">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Storefront size={22} /> Distributor Management</h2>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" className="text-xs gap-1 h-8" onClick={handleDownloadTemplate} data-testid="download-dist-template">
                <FileXls size={14} className="text-green-600" /> Template
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-xs gap-1 h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                onClick={async () => {
                  try {
                    const res = await api.get('/distributors/bulk/export-all', { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `all_distributors_${Date.now()}.xlsx`;
                    document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success('All distributors exported');
                  } catch (e) {
                    toast.error('Download failed');
                  }
                }}
                data-testid="download-all-distributors"
              >
                <FileXls size={14} /> Download All
              </Button>
              <Button
                size="sm"
                className="text-xs gap-1 h-8 bg-indigo-700 hover:bg-indigo-800 text-white"
                onClick={() => setBulkCreateOpen(true)}
                data-testid="open-bulk-create-dist"
              >
                <UploadSimple size={14} /> Bulk Create IDs
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-xs gap-1 h-8 border-purple-300 text-purple-700 hover:bg-purple-50"
                onClick={() => setResetPwdOpen(true)}
                data-testid="open-reset-passwords"
                title="Reset selected distributors' passwords to Patna@123"
              >
                <FileXls size={14} /> Reset Passwords
              </Button>
              <label className="inline-flex items-center gap-1 cursor-pointer px-3 h-8 text-xs border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                <UploadSimple size={14} className="text-blue-600" />
                {bulkUploading ? 'Uploading...' : 'Bulk Upload'}
                <input type="file" accept=".xlsx,.xls" onChange={handleBulkUpload} className="hidden" disabled={bulkUploading} data-testid="bulk-dist-upload" />
              </label>
              <Dialog open={distDialogOpen} onOpenChange={setDistDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs" data-testid="add-distributor-btn">
                    <Plus size={14} className="mr-1" /> Add
                  </Button>
                </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Distributor</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleDistributorSubmit} className="space-y-4">
                  <div>
                    <Label>Name</Label>
                    <Input value={distForm.name} onChange={e => setDistForm(p => ({ ...p, name: e.target.value }))} required data-testid="dist-name-input" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input type="email" value={distForm.email} onChange={e => setDistForm(p => ({ ...p, email: e.target.value }))} required data-testid="dist-email-input" />
                  </div>
                  <div>
                    <Label>Password</Label>
                    <Input type="password" value={distForm.password} onChange={e => setDistForm(p => ({ ...p, password: e.target.value }))} required minLength={6} data-testid="dist-password-input" />
                  </div>
                  <div>
                    <Label>Assign Agencies</Label>
                    <div className="border border-gray-200 rounded-md p-3 max-h-48 overflow-y-auto space-y-2 mt-1">
                      {agencies.filter(a => a.status !== 'pending_approval').map(ag => (
                        <label key={ag.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={distForm.agency_ids.includes(ag.id)}
                            onChange={() => toggleAgency(ag.id)}
                            className="w-4 h-4 text-emerald-600 rounded"
                            data-testid={`agency-check-${ag.id}`}
                          />
                          <span className="text-sm">{ag.name}</span>
                          <span className="text-xs text-gray-400 ml-auto">₹{ag.rate}</span>
                        </label>
                      ))}
                      {agencies.length === 0 && <p className="text-sm text-gray-400">No agencies found</p>}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{distForm.agency_ids.length} agency selected</p>
                  </div>
                  <div>
                    <Label>Assign Sales Person</Label>
                    <select value={distForm.sales_person_id} onChange={e => setDistForm(p => ({ ...p, sales_person_id: e.target.value }))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1" data-testid="dist-sp-select">
                      <option value="">-- No Sales Person --</option>
                      {salesPersons.map(sp => (
                        <option key={sp.id} value={sp.id}>{sp.name} ({sp.mobile || sp.email})</option>
                      ))}
                    </select>
                  </div>
                  <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="dist-submit-btn">
                    Create Distributor
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>

          {/* Bulk Upload Result */}
          {bulkResult && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-2">
              <p className="text-sm font-semibold text-gray-700">{bulkResult.message}</p>
              <p className="text-xs text-gray-500">Default Password: <span className="font-mono font-bold">{bulkResult.default_password}</span></p>
              {bulkResult.skipped?.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-red-600 font-semibold">Skipped ({bulkResult.skipped.length}):</p>
                  {bulkResult.skipped.map((s, i) => (
                    <p key={`skip-${s.row}-${i}`} className="text-xs text-red-500">Row {s.row}: {s.name} — {s.reason}</p>
                  ))}
                </div>
              )}
              <Button variant="ghost" size="sm" onClick={() => setBulkResult(null)} className="text-xs text-gray-400 mt-1">Dismiss</Button>
            </div>
          )}

          <div className="card-flat overflow-x-auto">
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Agencies</TableHead>
                  <TableHead>Sales Person</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {distributors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-gray-500">No distributors yet</TableCell>
                  </TableRow>
                ) : (
                  distributors.map(d => (
                    <TableRow key={d.id} data-testid={`dist-row-${d.id}`}>
                      <TableCell className="font-medium">
                        {d.agency_names?.length > 0 ? (
                          <>
                            <div className="text-gray-900" data-testid={`dist-agency-${d.id}`}>{d.agency_names[0]}{d.agency_names.length > 1 && <span className="text-xs text-gray-500 font-normal"> +{d.agency_names.length - 1}</span>}</div>
                            <div className="text-[11px] text-gray-500 font-normal">Owner: {d.name}</div>
                          </>
                        ) : (
                          <>
                            <div className="text-gray-900">{d.name}</div>
                            <div className="text-[11px] text-amber-600 font-normal">⚠️ No agency assigned</div>
                          </>
                        )}
                      </TableCell>
                      <TableCell>{d.email}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {d.agency_names?.map((name, i) => (
                            <span key={`agency-${name}-${i}`} className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">{name}</span>
                          ))}
                          {(!d.agency_names || d.agency_names.length === 0) && <span className="text-xs text-gray-400">None</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        {d.sales_person_name ? (
                          <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">{d.sales_person_name}</span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {d.status === 'blocked' ? (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded" title={d.blocked_reason}>Blocked</span>
                        ) : d.status === 'suspended' ? (
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded" title={d.blocked_reason}>Suspended</span>
                        ) : (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Active</span>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(d.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="sm" onClick={() => openEditUser(d)} className="text-blue-600 hover:text-blue-700" data-testid={`edit-dist-${d.id}`}>
                            <PencilSimple size={16} />
                          </Button>
                          {(d.status === 'approved' || !d.status) && (
                            <Button variant="ghost" size="sm" onClick={() => handleBlock(d.id, d.name)} className="text-orange-600 hover:text-orange-700" data-testid={`block-dist-${d.id}`} title="Block">
                              <Prohibit size={16} />
                            </Button>
                          )}
                          {(d.status === 'blocked' || d.status === 'suspended') && (
                            <Button variant="ghost" size="sm" onClick={() => handleUnblock(d.id, d.name)} className="text-green-600 hover:text-green-700" data-testid={`unblock-dist-${d.id}`} title="Unblock">
                              <CheckCircle size={16} />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteDistributor(d.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-dist-${d.id}`}>
                            <Trash size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Bulk Create Distributor IDs Dialog */}
      <BulkCreateDistributorsDialog
        open={bulkCreateOpen}
        onOpenChange={setBulkCreateOpen}
        onCreated={fetchData}
      />

      {/* Reset Distributor Passwords Dialog */}
      <ResetDistributorPasswordsDialog
        open={resetPwdOpen}
        onOpenChange={setResetPwdOpen}
      />

      {/* Sales Person Section — Owner/Admin Only */}
      {showSalesPersons && isOwner && (
        <div className="card-flat" data-testid="salesperson-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <UserCirclePlus size={22} className="text-indigo-600" />
              Sales Persons
            </h2>
            <Dialog open={spDialogOpen} onOpenChange={setSpDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2" data-testid="add-salesperson-btn">
                  <Plus size={18} /> Add Sales Person
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Create Sales Person</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                  <div><Label>Name</Label><Input value={spForm.name} onChange={e => setSpForm({ ...spForm, name: e.target.value })} placeholder="Sales Person Name" data-testid="sp-form-name" /></div>
                  <div><Label>Email</Label><Input type="email" value={spForm.email} onChange={e => setSpForm({ ...spForm, email: e.target.value })} placeholder="email@example.com" data-testid="sp-form-email" /></div>
                  <div><Label>Mobile</Label><Input value={spForm.mobile} onChange={e => setSpForm({ ...spForm, mobile: e.target.value })} placeholder="9876543210" data-testid="sp-form-mobile" /></div>
                  <div><Label>Password</Label><Input type="password" value={spForm.password} onChange={e => setSpForm({ ...spForm, password: e.target.value })} placeholder="Min 6 characters" data-testid="sp-form-password" /></div>
                  <div><Label>Commission Rate (per cylinder)</Label><Input type="number" value={spForm.commission_rate} onChange={e => setSpForm({ ...spForm, commission_rate: e.target.value })} placeholder="e.g., 5" data-testid="sp-form-commission" /></div>
                  <Button onClick={handleCreateSalesPerson} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="sp-form-create-btn">Create Sales Person</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="overflow-x-auto">
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead>Commission/Cyl</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {salesPersons.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-gray-500">No sales persons</TableCell></TableRow>
                ) : salesPersons.map(sp => (
                  <TableRow key={sp.id} data-testid={`sp-row-${sp.id}`}>
                    <TableCell className="font-medium">{sp.name}</TableCell>
                    <TableCell className="text-sm text-gray-500">{sp.email}</TableCell>
                    <TableCell className="text-sm text-gray-500">{sp.mobile || '-'}</TableCell>
                    <TableCell className="font-mono text-sm">{sp.commission_rate ? `₹${sp.commission_rate}` : '-'}</TableCell>
                    <TableCell><span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">{sp.status || 'Active'}</span></TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-7 w-7 p-0" onClick={() => handleDeleteSalesPerson(sp.id)} data-testid={`sp-delete-${sp.id}`}>
                        <Trash size={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Accountant Section — Owner Only */}
      {showAccountants && isOwner && (
        <div className="card-flat" data-testid="accountant-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Calculator size={22} className="text-teal-600" />
              Accountants (CA)
              <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded">Read-Only Access</span>
            </h2>
            <Dialog open={accDialogOpen} onOpenChange={setAccDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-teal-600 hover:bg-teal-700 text-white gap-2" data-testid="add-accountant-btn">
                  <Plus size={18} /> Add Accountant
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Create Accountant</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                  <div><Label>Name</Label><Input value={accForm.name} onChange={e => setAccForm({ ...accForm, name: e.target.value })} placeholder="CA Name" data-testid="acc-name" /></div>
                  <div><Label>Email</Label><Input type="email" value={accForm.email} onChange={e => setAccForm({ ...accForm, email: e.target.value })} placeholder="ca@example.com" data-testid="acc-email" /></div>
                  <div><Label>Mobile</Label><Input value={accForm.mobile} onChange={e => setAccForm({ ...accForm, mobile: e.target.value })} placeholder="9876543210" data-testid="acc-mobile" /></div>
                  <div><Label>Password</Label><Input type="password" value={accForm.password} onChange={e => setAccForm({ ...accForm, password: e.target.value })} placeholder="Min 6 characters" data-testid="acc-password" /></div>
                  <Button onClick={handleCreateAccountant} className="w-full bg-teal-600 hover:bg-teal-700 text-white" data-testid="acc-create-btn">Create Accountant</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="overflow-x-auto">
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accountants.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-gray-500">No accountants</TableCell></TableRow>
                ) : accountants.map(a => (
                  <TableRow key={a.id} data-testid={`acc-row-${a.id}`}>
                    <TableCell className="font-medium">{a.name}</TableCell>
                    <TableCell className="text-sm text-gray-500">{a.email}</TableCell>
                    <TableCell><span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded">Accountant</span></TableCell>
                    <TableCell><span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Active</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="text-[10px] text-gray-400 mt-2">Accountant can view all reports, ledgers, bookings — but cannot edit anything.</p>
        </div>
      )}

      {/* Vendors (Gift Card / Recharge / Electricity) — Admin & CA */}
      {showVendorsBlock && (isOwner || user?.role === 'accountant') && (
        <VendorsSection isReadOnly={user?.role === 'accountant'} />
      )}

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit User — {editingUser?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditUser} className="space-y-4 mt-2">
            <div className="text-xs bg-gray-50 px-3 py-2 rounded">
              <span className="font-semibold">Role:</span> <span className="uppercase text-gray-600">{editingUser?.role}</span>
            </div>
            <div>
              <Label className="text-sm">Name</Label>
              <Input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} data-testid="edit-user-name" />
            </div>
            <div>
              <Label className="text-sm">Email</Label>
              <Input type="email" value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} data-testid="edit-user-email" />
            </div>
            <div>
              <Label className="text-sm">Mobile</Label>
              <Input value={editForm.mobile} onChange={e => setEditForm(p => ({ ...p, mobile: e.target.value }))} placeholder="Mobile number" data-testid="edit-user-mobile" />
            </div>
            {editingUser?.role === 'distributor' && (
              <>
                <div>
                  <Label className="text-sm">Agencies <span className="text-gray-400 text-xs">(at least 1)</span></Label>
                  <div className="max-h-40 overflow-y-auto border rounded p-2 space-y-1 bg-white" data-testid="edit-dist-agencies">
                    {agencies.filter(a => a.status !== 'pending_approval').map(ag => (
                      <label key={ag.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 px-1 py-0.5 rounded">
                        <input
                          type="checkbox"
                          checked={editForm.agency_ids.includes(ag.id)}
                          onChange={() => toggleEditAgency(ag.id)}
                          data-testid={`edit-dist-agency-${ag.id}`}
                        />
                        <span>{ag.name}</span>
                      </label>
                    ))}
                    {agencies.length === 0 && <p className="text-xs text-gray-400">No agencies found</p>}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{editForm.agency_ids.length} selected</p>
                </div>
                <div>
                  <Label className="text-sm">Sales Person <span className="text-gray-400 text-xs">(optional)</span></Label>
                  <select
                    value={editForm.sales_person_id}
                    onChange={e => setEditForm(p => ({ ...p, sales_person_id: e.target.value }))}
                    className="form-input w-full"
                    data-testid="edit-dist-sales-person"
                  >
                    <option value="">— None —</option>
                    {salesPersons.map(sp => (
                      <option key={sp.id} value={sp.id}>{sp.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-sm">Low Balance Alert Threshold <span className="text-gray-400 text-xs">(₹, blank = default ₹5,000)</span></Label>
                  <Input
                    type="number"
                    min="0"
                    step="100"
                    value={editForm.low_balance_threshold}
                    onChange={e => setEditForm(p => ({ ...p, low_balance_threshold: e.target.value }))}
                    placeholder="e.g. 10000"
                    data-testid="edit-dist-low-balance-threshold"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">
                    Dashboard pe is distributor ka net balance is amount se neeche aane par red alert dikhega.
                  </p>
                </div>
              </>
            )}
            <div>
              <Label className="text-sm">New Password <span className="text-gray-400">(blank = no change)</span></Label>
              <Input type="password" value={editForm.password} onChange={e => setEditForm(p => ({ ...p, password: e.target.value }))} placeholder="Leave blank to keep current" data-testid="edit-user-password" />
            </div>
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" data-testid="edit-user-submit">
              Update Profile
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Sub-Staff Create Dialog */}
      <Dialog open={subStaffDialogOpen} onOpenChange={setSubStaffDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Sub-Staff {expandedTeamLead && staff.find(s => s.id === expandedTeamLead) ? `under ${staff.find(s => s.id === expandedTeamLead)?.name}` : ''}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateSubStaff} className="space-y-4 mt-2">
            <div>
              <Label className="text-sm">Name <span className="text-red-500">*</span></Label>
              <Input value={subStaffForm.name} onChange={e => setSubStaffForm(p => ({ ...p, name: e.target.value }))} placeholder="Sub-staff name" required data-testid="sub-staff-name" />
            </div>
            <div>
              <Label className="text-sm">Email <span className="text-red-500">*</span></Label>
              <Input type="email" value={subStaffForm.email} onChange={e => setSubStaffForm(p => ({ ...p, email: e.target.value }))} placeholder="Login email" required data-testid="sub-staff-email" />
            </div>
            <div>
              <Label className="text-sm">Password <span className="text-red-500">*</span></Label>
              <Input type="password" value={subStaffForm.password} onChange={e => setSubStaffForm(p => ({ ...p, password: e.target.value }))} placeholder="Login password" required data-testid="sub-staff-password" />
            </div>
            <div>
              <Label className="text-sm">Mobile</Label>
              <Input value={subStaffForm.mobile} onChange={e => setSubStaffForm(p => ({ ...p, mobile: e.target.value }))} placeholder="Optional" />
            </div>
            <Button type="submit" className="w-full bg-violet-600 hover:bg-violet-700" data-testid="sub-staff-submit">
              Create Sub-Staff
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
